import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_BASE_URL || "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

const ROLE_LABELS = {
  super_admin:   "Super Admin",
  finance_admin: "Finance Admin",
  ops_admin:     "Ops Admin",
};
const ROLE_OPTIONS = ["ops_admin", "finance_admin", "super_admin"];

export default function CockpitTeam() {
  const [admins,  setAdmins]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg,     setMsg]     = useState(null); // { type, text }
  const [showCreate, setShowCreate] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // current admin (to prevent self-actions in UI)
  let me = {};
  try { me = JSON.parse(localStorage.getItem("adminUser") || "{}"); } catch (e) {}

  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", role: "ops_admin" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/admins`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const d = await res.json();
      if (d.ok) setAdmins(d.admins || []);
      else setMsg({ type: "error", text: d.error || "Failed to load admins" });
    } catch (e) {
      setMsg({ type: "error", text: "Network error loading admins" });
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const createAdmin = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      flash("error", "All fields are required");
      return;
    }
    if (form.password.length < 8) {
      flash("error", "Password must be at least 8 characters");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/admins`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (d.ok) {
        flash("success", `✅ ${d.message}`);
        setShowCreate(false);
        setForm({ firstName: "", lastName: "", email: "", password: "", role: "ops_admin" });
        load();
      } else {
        flash("error", `❌ ${d.error || "Create failed"}`);
      }
    } catch (e) {
      flash("error", "Network error creating admin");
    }
    setCreating(false);
  };

  const changeRole = async (admin, newRole) => {
    if (newRole === admin.role) return;
    if (!window.confirm(`Change ${admin.email} from ${ROLE_LABELS[admin.role]} to ${ROLE_LABELS[newRole]}?`)) return;
    setBusyId(admin.id);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/users/${admin.id}/role`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ role: newRole }),
      });
      const d = await res.json();
      if (d.ok) { flash("success", `✅ Role updated to ${ROLE_LABELS[newRole]}`); load(); }
      else flash("error", `❌ ${d.error || "Role change failed"}`);
    } catch (e) {
      flash("error", "Network error changing role");
    }
    setBusyId(null);
  };

  const toggleStatus = async (admin) => {
    const newStatus = admin.status === "active" ? "suspended" : "active";
    const verb = newStatus === "suspended" ? "Suspend" : "Reactivate";
    if (!window.confirm(`${verb} ${admin.email}?`)) return;
    setBusyId(admin.id);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/admins/${admin.id}/status`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const d = await res.json();
      if (d.ok) { flash("success", `✅ ${d.message}`); load(); }
      else flash("error", `❌ ${d.error || "Status change failed"}`);
    } catch (e) {
      flash("error", "Network error changing status");
    }
    setBusyId(null);
  };

  const roleBadge = (role) => {
    const base = "px-2 py-0.5 rounded text-xs font-semibold";
    if (role === "super_admin")   return <span className={`${base} bg-purple-100 text-purple-700`}>Super Admin</span>;
    if (role === "finance_admin") return <span className={`${base} bg-blue-100 text-blue-700`}>Finance Admin</span>;
    return <span className={`${base} bg-gray-100 text-gray-700`}>Ops Admin</span>;
  };

  const statusBadge = (s) => {
    const base = "px-2 py-0.5 rounded text-xs font-semibold";
    if (s === "active") return <span className={`${base} bg-green-100 text-green-700`}>active</span>;
    return <span className={`${base} bg-red-100 text-red-700`}>{s}</span>;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Team & Admins</h1>
          <p className="text-sm text-gray-500">Manage cockpit administrators, roles, and access. All changes are audit-logged.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded"
        >
          + Add Admin
        </button>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-2 rounded text-sm ${msg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {msg.text}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Added</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No admins found.</td></tr>
            ) : admins.map(a => {
              const isMe = a.id === me.id;
              return (
                <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-800">{a.first_name} {a.last_name} {isMe && <span className="text-xs text-gray-400">(you)</span>}</td>
                  <td className="px-4 py-3 text-gray-700">{a.email}</td>
                  <td className="px-4 py-3">{roleBadge(a.role)}</td>
                  <td className="px-4 py-3">{statusBadge(a.status)}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <select
                        value={a.role}
                        disabled={isMe || busyId === a.id}
                        onChange={(e) => changeRole(a, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs disabled:opacity-40"
                        title={isMe ? "You can't change your own role" : "Change role"}
                      >
                        {ROLE_OPTIONS.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                      <button
                        onClick={() => toggleStatus(a)}
                        disabled={isMe || busyId === a.id}
                        className={`text-xs px-2 py-1 rounded disabled:opacity-40 ${a.status === "active" ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}
                        title={isMe ? "You can't change your own status" : ""}
                      >
                        {a.status === "active" ? "Suspend" : "Reactivate"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Add New Admin</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-3">
              <div className="flex gap-3">
                <input className="border border-gray-300 rounded px-3 py-2 text-sm w-1/2" placeholder="First name"
                  value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                <input className="border border-gray-300 rounded px-3 py-2 text-sm w-1/2" placeholder="Last name"
                  value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
              </div>
              <input className="border border-gray-300 rounded px-3 py-2 text-sm w-full" placeholder="Email" type="email"
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <input className="border border-gray-300 rounded px-3 py-2 text-sm w-full" placeholder="Temporary password (min 8 chars)" type="text"
                value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              <select className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
                value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
              <p className="text-xs text-gray-400">The new admin should change this password after first login.</p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={createAdmin} disabled={creating}
                className="px-4 py-2 text-sm rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
                {creating ? "Creating…" : "Create Admin"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
